import React, { useEffect, useState } from 'react'
import {
    Grid,
    Box,
    Typography,
    Button,
} from "@mui/material"
import { ExternalLink, Disc3, Video, Scale, Link2, ListMusic, UserPlus, FileText, Ticket, ListChecks, Wallet, Music, LayoutGrid } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { countMeterAdmin } from '../Common/actions'

const queueItems = [
    { label: 'Review Releases', countKey: 'in_review', path: '/releases/review', icon: <Disc3 size={16} /> },
    { label: 'Video Releases', countKey: 'video_releases', path: '/video_releases/review', icon: <Video size={16} /> },
    { label: 'UGC Claims', countKey: 'ugc_claim', path: '/ugc_claims', icon: <Scale size={16} /> },
    { label: 'Profile Linking', countKey: 'profile_linking', path: '/profile_linking', icon: <Link2 size={16} /> },
    { label: 'Playlist Pitching', countKey: 'pitch_playlist', path: '/playlist_pitching', icon: <ListMusic size={16} /> },
    { label: 'Label Requests', countKey: 'label_requests', path: '/label-requests', icon: <UserPlus size={16} /> },
    { label: 'Report Requests', countKey: 'reports_requested', path: '/report_requests', icon: <FileText size={16} /> },
    { label: 'Tickets', countKey: 'open_tickets', path: '/tickets', icon: <Ticket size={16} /> },
    { label: 'Whitelisting', countKey: 'whitelisting', path: '/whitelisting', icon: <ListChecks size={16} /> },
    { label: 'Requested Payouts', countKey: 'withdrawl_requested', path: '/payout', icon: <Wallet size={16} /> },
    { label: 'Lyrics Distribution', countKey: 'distribute_lyrics', path: '/lyrics_distribution', icon: <Music size={16} /> },
]

// Continuous green → yellow → red based on count relative to max
const getCardColors = (count, maxCount) => {
    const ratio = maxCount > 0 ? Math.min(count / maxCount, 1) : 0
    // Hue: 130 (green) → 45 (yellow) at 0.5 → 0 (red) at 1
    const hue = count === 0 ? 130 : (
        ratio <= 0.5
            ? 130 - (130 - 45) * (ratio / 0.5)
            : 45 - 45 * ((ratio - 0.5) / 0.5)
    )
    const h = Math.round(hue)
    return {
        accent: count === 0 ? 'hsl(130, 40%, 48%)' : `hsl(${h}, 42%, ${Math.round(50 - ratio * 6)}%)`,
        bg: `hsl(${h}, 20%, ${Math.round(20 - ratio * 2)}%)`,
        border: `hsl(${h}, 25%, ${Math.round(28 - ratio * 4)}%)`,
    }
}


const QueueCard = ({ item, count, maxCount, onClick }) => {
    const { accent, bg, border } = getCardColors(count, maxCount)

    return (
        <Box
            onClick={onClick}
            sx={{
                background: bg,
                border: `1px solid ${border}`,
                borderRadius: '2px',
                p: 1.8,
                height: 110,
                display: 'flex',
                flexDirection: 'column',
                cursor: 'pointer',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                    borderColor: accent,
                    boxShadow: `0 4px 20px ${accent}33`,
                    transform: 'translateY(-3px)',
                    '& .queue-icon': {
                        transform: 'scale(1.1)',
                    },
                },
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1, flexShrink: 0 }}>
                <Box
                    className="queue-icon"
                    sx={{
                        width: 22,
                        height: 22,
                        borderRadius: '5px',
                        backgroundColor: `${accent}22`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: accent,
                        transition: 'transform 0.25s ease',
                    }}
                >
                    {item.icon}
                </Box>
                <Typography sx={{
                    fontSize: '0.68rem',
                    color: '#64748B',
                    fontWeight: 600,
                    letterSpacing: '0.04em',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }}>
                    {item.label}
                </Typography>
            </Box>

            <Typography sx={{
                fontSize: '1.5rem',
                fontWeight: 800,
                color: count === 0 ? '#4A5068' : accent,
                lineHeight: 1,
                flexGrow: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
            }}>
                {count}
            </Typography>
        </Box>
    )
}

export default function Dashboard(props) {
    const navigate = useNavigate()
    const [queueCounts, setQueueCounts] = useState({})

    useEffect(() => {
        countMeterAdmin()
            .then((res) => {
                setQueueCounts(res.body || {})
            })
            .catch(() => {})
    }, [])

    const maxQueueCount = Math.max(...queueItems.map(i => queueCounts[i.countKey] || 0), 1)

    return (
        <>
            {/* Workload Overview */}
            <Box sx={{ mb: 3 }}>
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2.5,
                }}>
                    <Typography sx={{
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        color: '#4A5068',
                    }}>
                        Workload Overview
                    </Typography>
                    <Button
                        onClick={() => window.open('/live-analytics', '_blank')}
                        startIcon={<ExternalLink size={16} />}
                        sx={{
                            color: '#F1F5F9',
                            backgroundColor: 'rgba(108, 99, 255, 0.12)',
                            border: '1px solid rgba(108, 99, 255, 0.25)',
                            borderRadius: '10px',
                            px: 2,
                            py: 0.8,
                            fontSize: '0.82rem',
                            fontWeight: 600,
                            textTransform: 'none',
                            letterSpacing: '0.01em',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                backgroundColor: 'rgba(108, 99, 255, 0.22)',
                                borderColor: 'rgba(108, 99, 255, 0.4)',
                                boxShadow: '0 0 16px rgba(108, 99, 255, 0.15)',
                            },
                        }}
                    >
                        Open Live Analytics
                    </Button>
                </Box>
                <Grid container spacing={2}>
                    {queueItems.map((item) => (
                        <Grid item xs={6} sm={4} md={3} key={item.countKey}>
                            <QueueCard
                                item={item}
                                count={queueCounts[item.countKey] || 0}
                                maxCount={maxQueueCount}
                                onClick={() => navigate(item.path)}
                            />
                        </Grid>
                    ))}
                    <Grid item xs={6} sm={4} md={3}>
                        <QueueCard
                            item={{ label: 'Total Pending', countKey: '_total', icon: <LayoutGrid size={16} /> }}
                            count={queueItems.reduce((sum, i) => sum + (queueCounts[i.countKey] || 0), 0)}
                            maxCount={maxQueueCount}
                            onClick={() => {}}
                        />
                    </Grid>
                </Grid>
            </Box>
        </>
    )
}
